import React, { useMemo, useState, useTransition } from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery, useMutation } from 'react-relay'
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import { Archive, Check, Pencil, Plus, X } from 'lucide-react'
import { toast } from 'react-toastify'
import { AdminRole } from '../roles'
import { AdminUserInput, UserFormDialog } from './UserFormDialog'
import { ConfirmArchiveDialog } from '../components/ConfirmArchiveDialog'
import { AdminQuickFilterToolbar } from '../AdminQuickFilterToolbar'
import { adminDataGridSx } from '../dataGridSx'
import { archiveIconSx } from '../styles'
import { UsersTabQuery } from './__generated__/UsersTabQuery.graphql'
import { UsersTabArchiveMutation } from './__generated__/UsersTabArchiveMutation.graphql'

export const usersTabQuery = graphql`
  query UsersTabQuery($input: AdminListUsersRequest!) {
    adminListUsers(input: $input) {
      count
      data {
        id
        email
        fullName
        firstName
        lastName
        superuser
        createdAt
        featureFlagIds
        account {
          id
          name
        }
        accountRoles {
          role
        }
        userBrands {
          id
          brand {
            id
            name
          }
          roles {
            role
          }
        }
      }
    }
  }
`

const archiveUserMutation = graphql`
  mutation UsersTabArchiveMutation($input: DeleteRequest!) {
    archiveUser(input: $input) {
      id
    }
  }
`

interface UserRow {
  id: string
  email: string
  name: string
  createdAt: unknown
  account: string
  brands: string
  superuser: boolean
  editable: AdminUserInput
}

interface UserBrandAccess {
  brand: {
    name: string
  }
}

export const formatUserBrandAccess = ({
  superuser,
  userBrands,
}: {
  superuser: boolean
  userBrands: readonly UserBrandAccess[]
}) => {
  if (superuser) return 'All Brands'
  if (userBrands.length === 0) return '—'
  return userBrands.map((ub) => ub.brand.name).join(', ')
}

export const UsersTab = () => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [refreshKey, setRefreshKey] = useState(0)
  const [search, setSearch] = useState('')
  const [isPending, startTransition] = useTransition()
  const [formUser, setFormUser] = useState<AdminUserInput | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<{
    id: string
    email: string
  } | null>(null)

  const data = useLazyLoadQuery<UsersTabQuery>(
    usersTabQuery,
    {
      input: {
        pageNumber: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: search || undefined,
      },
    },
    { fetchPolicy: 'network-only', fetchKey: String(refreshKey) }
  )

  const [archiveUser, archiving] =
    useMutation<UsersTabArchiveMutation>(archiveUserMutation)

  // Refresh must be a transition: it bumps the fetchKey of a network-only
  // query, which suspends this tab. Without startTransition the Suspense
  // fallback replaces the tab mid dialog-close animation, stranding a
  // half-closed MUI Dialog on screen.
  const refresh = () => startTransition(() => setRefreshKey((key) => key + 1))

  const handlePaginationChange = (model: GridPaginationModel) =>
    startTransition(() => setPaginationModel(model))

  const handleFilterChange = (model: GridFilterModel) =>
    startTransition(() => {
      setSearch((model.quickFilterValues ?? []).join(' '))
      setPaginationModel((prev) => ({ ...prev, page: 0 }))
    })

  const rows: UserRow[] = useMemo(
    () =>
      (data.adminListUsers.data ?? []).map((user) => ({
        id: user.id,
        email: user.email ?? '—',
        name:
          user.fullName ||
          [user.firstName, user.lastName].filter(Boolean).join(' ') ||
          '—',
        createdAt: user.createdAt,
        account: user.account?.name ?? '—',
        brands: formatUserBrandAccess(user),
        superuser: user.superuser,
        editable: {
          id: user.id,
          email: user.email ?? '',
          firstName: user.firstName ?? '',
          lastName: user.lastName ?? '',
          fullName: user.fullName ?? '',
          superuser: user.superuser,
          accountId: user.account?.id ?? '',
          accountRoles: user.accountRoles.map((r) => r.role as AdminRole),
          brandAccess: user.userBrands.map((ub) => ({
            brandId: ub.brand.id,
            roles: ub.roles.map((r) => r.role as AdminRole),
          })),
          featureFlagIds: [...user.featureFlagIds],
        },
      })),
    [data]
  )

  const openCreate = () => {
    setFormUser(null)
    setFormOpen(true)
  }

  const openEdit = (row: UserRow) => {
    setFormUser(row.editable)
    setFormOpen(true)
  }

  const handleArchive = () => {
    if (!archiveTarget) return
    archiveUser({
      variables: { input: { id: archiveTarget.id } },
      onCompleted: () => {
        toast.success('User archived.')
        setArchiveTarget(null)
        refresh()
      },
      onError: (error) => {
        console.error(error)
        toast.error(error.message || 'Unable to archive user.')
      },
    })
  }

  const columns: GridColDef<UserRow>[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    {
      field: 'superuser',
      headerName: 'Super User',
      width: 100,
      type: 'boolean',
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<UserRow>) => (
        <Box
          component="span"
          sx={{
            color: params.value ? 'primary.main' : '#fa7861',
            display: 'inline-flex',
          }}
        >
          {params.value ? <Check size={18} /> : <X size={18} />}
        </Box>
      ),
    },
    { field: 'account', headerName: 'Account', width: 190 },
    { field: 'brands', headerName: 'Brand Access', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams<UserRow>) => (
        <div className="flex h-full w-full items-center justify-center gap-3">
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={() => openEdit(params.row)}
            >
              <Pencil size={16} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Archive">
            <IconButton
              size="small"
              sx={archiveIconSx}
              onClick={() =>
                setArchiveTarget({ id: params.row.id, email: params.row.email })
              }
            >
              <Archive size={16} />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={openCreate}
        >
          Add User
        </Button>
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        autoHeight
        loading={isPending}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[25, 50, 100]}
        rowCount={data.adminListUsers.count}
        disableRowSelectionOnClick
        disableColumnMenu
        filterMode="server"
        onFilterModelChange={handleFilterChange}
        slots={{ toolbar: AdminQuickFilterToolbar }}
        sx={adminDataGridSx}
      />

      <UserFormDialog
        open={formOpen}
        user={formUser}
        onClose={() => setFormOpen(false)}
        onSaved={refresh}
      />

      <ConfirmArchiveDialog
        open={archiveTarget != null}
        title={`Archive '${archiveTarget?.email}'?`}
        message={`Archiving this user will prevent them from logging in or viewing any data, and their record will be hidden from the app. The data is retained in the database and can be restored later.`}
        loading={archiving}
        onCancel={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
      />
    </div>
  )
}

export default UsersTab
